import { useQuery } from '@apollo/client'
import {
  FetchViewQueryResultsDocument,
  type FetchViewQueryResultsQuery,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-common'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryRecordNode,
  ViewQueryResult,
} from '@salescore/core'
import { message } from 'antd'
import { Array, pipe } from 'effect'
import { isNotNull, isNotNullish } from 'effect/Predicate'
import { useSetRecoilState } from 'recoil'

import { useMustacheParameter } from '../../../hooks/useMustacheParameter'
import { useAggregationQueries } from '../../viewConfig'
import { errorsAtom } from '../atoms'

const toAggregationQueryResults = (data: FetchViewQueryResultsQuery | undefined) =>
  pipe(data?.viewQueryResults ?? [], Array.filter(isNotNull))

const toAggregationQueryErrors = (data: FetchViewQueryResultsQuery | undefined) =>
  pipe(
    toAggregationQueryResults(data),
    Array.map((result) => result.error),
    Array.filter(isNotNullish),
  )

const toAggregationQueryRecords = (data: FetchViewQueryResultsQuery | undefined) =>
  toAggregationQueryResults(data)
    .flatMap((x) => x.result)
    .filter(isNotNullish)

interface ResultType {
  refetch: () => Promise<void>
  loading: boolean
  aggregationQueryResults: ViewQueryResult[]
  aggregationQueryErrors: string[]
  aggregationQueryRecords: ViewQueryRecordNode[]
}

export const useRefetchAggregationQueryResult = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
): ResultType => {
  const mustacheParameter = useMustacheParameter()
  const queries = useAggregationQueries(compiledConfig)
  const {
    data,
    loading,
    refetch: _refetch,
  } = useQuery(FetchViewQueryResultsDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
      viewQueries: queries,
      mustacheParameter,
      viewId: view.id,
    },
  })
  const setErrors = useSetRecoilState(errorsAtom)

  const refetch = async (): Promise<void> => {
    try {
      const { data: result } = await _refetch()
      const viewQueryResults = result.viewQueryResults.filter(isNotNullish)
      const errors = viewQueryResults.map((x) => x.error).filter(isNotNullish)
      if (errors.length > 0) {
        for (const error of errors) {
          void message.error(error)
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        setErrors([error.message])
      }
    }
  }

  return {
    refetch,
    loading,
    aggregationQueryResults: toAggregationQueryResults(data),
    aggregationQueryErrors: toAggregationQueryErrors(data),
    aggregationQueryRecords: toAggregationQueryRecords(data),
  }
}
